import * as React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { Booking, Child, Event, SocialSecurityFund } from '../api/client'
import { deleteBooking, fetchBookings, fetchChildren, fetchSocialSecurityFunds, fetchUpcomingEvents, updateBooking } from '../api/client'
import { BookingInfoSection } from '../components/BookingInfoSection'
import { NavigationBar } from '../components/NavigationBar'
import { SocialSecurityFundSelect, SocialSecuritySubFundSelect } from '../components/SocialSecuritySelect'

// Cancel Confirmation Modal Component
function CancelConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isCancelling
}: { 
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isCancelling: boolean;
}) {
  const { t } = useTranslation(['bookings', 'common']);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg border-4 border-black shadow-neobrutalism w-full max-w-md mx-4 p-4 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-black text-neobrutalism-text">
            {t('modal.cancelBookingTitle', { ns: 'bookings', defaultValue: 'Cancel Booking' })}
          </h2>
          <button 
            onClick={onClose} 
            className="text-neobrutalism-text hover:text-neobrutalism-accent"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <p className="mb-6 text-gray-700">
          {t('modal.cancelBookingMessage', { ns: 'bookings', defaultValue: 'Are you sure you want to cancel this booking? This action cannot be undone.' })}
        </p>
        
        <div className="flex justify-end space-x-4">
          <button 
            onClick={onClose}
            className="btn-neobrutalism bg-gray-200 text-gray-800 py-2 px-4 flex items-center justify-center"
          >
            {t('buttons.cancel', { ns: 'common', defaultValue: 'Cancel' })}
          </button>
          <button 
            onClick={onConfirm}
            disabled={isCancelling}
            className="btn-neobrutalism bg-neobrutalism-red py-2 px-4 flex items-center justify-center"
          >
            {isCancelling ? (
              <>
                <svg className="animate-spin mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('buttons.cancelling', { ns: 'bookings', defaultValue: 'Cancelling...' })}
              </>
            ) : (
              t('modal.confirmCancel', { ns: 'bookings', defaultValue: 'Yes, Cancel Booking' })
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function EditBookingPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation(['bookings', 'common'])
  
  const [booking, setBooking] = useState<Booking | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [friendError, setFriendError] = useState<string | null>(null)
  const [socialSecurityFundError, setSocialSecurityFundError] = useState<string | null>(null)
  const [children, setChildren] = useState<Child[]>([])
  const [socialSecurityFunds, setSocialSecurityFunds] = useState<SocialSecurityFund[]>([])
  
  // Form fields
  const [bookingId, setBookingId] = useState<number | null>(null)
  const [childId, setChildId] = useState<number | null>(null)
  const [eventId, setEventId] = useState<number | null>(null)
  const [status, setStatus] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [friendRegistrationNumbers, setFriendRegistrationNumbers] = useState<string[]>([])
  const [originalFriendRegistrationNumbers, setOriginalFriendRegistrationNumbers] = useState<string[]>([])
  const [friendRegistrationInput, setFriendRegistrationInput] = useState<string>('')
  const [copySuccess, setCopySuccess] = useState<boolean>(false)
  const [selectedSocialSecurityFund, setSelectedSocialSecurityFund] = useState<number | null>(null)
  const [selectedSocialSecuritySubFund, setSelectedSocialSecuritySubFund] = useState<number | null>(null)
  const [dypaProtocolNumber, setDypaProtocolNumber] = useState<string>('')
  const [dypaProtocolError, setDypaProtocolError] = useState<string | null>(null)

  // New state for modal and cancellation
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  
  // New state for arrival timeslots
  const [events, setEvents] = useState<Event[]>([])
  const [selectedArrivalTimeslot, setSelectedArrivalTimeslot] = useState<number | null>(null)
  
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return
      
      setLoading(true)
      setError(null)
      
      try {
        // Fetch booking data and other necessary data in parallel
        const [bookingsData, childrenData, fundsData, eventsData] = await Promise.all([
          fetchBookings(),
          fetchChildren(),
          fetchSocialSecurityFunds(),
          fetchUpcomingEvents()
        ])
        
        const currentBooking = bookingsData.find(b => b.id === parseInt(id, 10))
        
        if (!currentBooking) {
          setError(t('errors.bookingNotFound', { ns: 'bookings' }))
          setLoading(false)
          return
        }
        
        // Ensure the booking has friend_registration_numbers initialized
        if (!currentBooking.friend_registration_numbers) {
          currentBooking.friend_registration_numbers = [];
        }
        
        // Initialize social security fields if they don't exist
        if (currentBooking.social_security_fund_id === undefined) {
          // Check if there's a nested social_security_fund object
          if (currentBooking.social_security_fund && typeof currentBooking.social_security_fund === 'object') {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.social_security_fund_id = currentBooking.social_security_fund.id;
          } else {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.social_security_fund_id = null;
          }
        }
        
        if (currentBooking.social_security_sub_fund_id === undefined) {
          // Check if there's a nested social_security_sub_fund object
          if (currentBooking.social_security_sub_fund && typeof currentBooking.social_security_sub_fund === 'object') {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.social_security_sub_fund_id = currentBooking.social_security_sub_fund.id;
          } else {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.social_security_sub_fund_id = null;
          }
        }
        
        if (currentBooking.dypa_protocol_number === undefined) {
          currentBooking.dypa_protocol_number = '';
        }
        
        // Initialize arrival timeslot fields
        if (currentBooking.arrival_timeslot_id === undefined) {
          if (currentBooking.arrival_timeslot && typeof currentBooking.arrival_timeslot === 'object') {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.arrival_timeslot_id = currentBooking.arrival_timeslot.id;
          } else {
            // @ts-ignore - We know this is safe because we're initializing the property
            currentBooking.arrival_timeslot_id = null;
          }
        }
        
        setBooking(currentBooking)
        setChildren(childrenData || [])
        setSocialSecurityFunds(fundsData || [])
        setEvents(eventsData || [])
        
        // Set social security fund values - now using null check instead of undefined
        if (currentBooking.social_security_fund_id !== null && currentBooking.social_security_fund_id !== undefined) {
          setSelectedSocialSecurityFund(currentBooking.social_security_fund_id);
          
          // Find the fund in the fetched funds data
          const fund = fundsData.find(f => f.id === currentBooking.social_security_fund_id);
          if (fund) {
            // If there's also a sub-fund ID, set it
            if (currentBooking.social_security_sub_fund_id !== null && currentBooking.social_security_sub_fund_id !== undefined) {
              setSelectedSocialSecuritySubFund(currentBooking.social_security_sub_fund_id);
            }
          }
        } else {
          // Explicitly set to null to ensure the state is initialized
          setSelectedSocialSecurityFund(null);
          setSelectedSocialSecuritySubFund(null);
        }
        
        // Set arrival timeslot if it exists
        if (currentBooking.arrival_timeslot_id !== null && currentBooking.arrival_timeslot_id !== undefined) {
          setSelectedArrivalTimeslot(currentBooking.arrival_timeslot_id);
        } else {
          setSelectedArrivalTimeslot(null);
        }
        
        // Set DYPA protocol number if it exists
        if (currentBooking.dypa_protocol_number) {
          setDypaProtocolNumber(currentBooking.dypa_protocol_number);
        } else {
          setDypaProtocolNumber('');
        }
        
        // If there are friend registration numbers, set them
        if (currentBooking.friend_registration_numbers && currentBooking.friend_registration_numbers.length > 0) {
          setFriendRegistrationNumbers([...currentBooking.friend_registration_numbers])
          // Store the original friend registration numbers to compare later
          setOriginalFriendRegistrationNumbers([...currentBooking.friend_registration_numbers])
        } else {
          setFriendRegistrationNumbers([]);
          setOriginalFriendRegistrationNumbers([]);
        }
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'bookings' }))
      } finally {
        setLoading(false)
      }
    }
    
    fetchData()
  }, [id, t])
  
  // Reset sub-fund when fund changes, but only after initial load and only when manually changed
  useEffect(() => {
    // Skip this effect during the initial load or if there's no booking yet
    if (!booking) return;
    
    // Get the booking's fund ID, defaulting to null if undefined
    const bookingFundId = booking.social_security_fund_id !== undefined ? booking.social_security_fund_id : null;
    
    // Skip this effect if the selected fund is the same as the booking's fund
    // This prevents resetting during the initial load
    if (selectedSocialSecurityFund === bookingFundId) {
      return;
    }
    
    // Only reset if the fund has actually changed (not during initial load)
    setSelectedSocialSecuritySubFund(null);
  }, [selectedSocialSecurityFund, booking]);
  
  // Get the selected fund object
  const selectedFund = socialSecurityFunds.find(fund => fund.id === selectedSocialSecurityFund)
  
  // Get the sub-funds for the selected fund
  const subFunds = selectedFund?.sub_funds || []
  
  // Check if the selected fund has sub-funds
  const hasSocialSecuritySubFunds = selectedFund && selectedFund.sub_funds.length > 0
  
  // Get the current event and its arrival timeslots
  const currentEvent = booking ? events.find(event => event.id === booking.event) : null
  const arrivalTimeslots = currentEvent?.arrival_timeslots || []
  
  // Check if arrival timeslot selection should be enabled
  const canSelectArrivalTimeslot = booking && (booking.status === 'confirmed' || booking.status === 'completed')
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!booking || !id) return
    
    // Validate social security fund and sub-fund
    if (selectedSocialSecurityFund && hasSocialSecuritySubFunds && !selectedSocialSecuritySubFund) {
      // Don't set the top error message, as we're showing it under the field
      return
    }
    
    // Validate DYPA protocol number if ΔΥΠΑ is selected and value is provided
    if (selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && dypaProtocolNumber) {
      const { isValid, error } = validateDypaProtocolNumber(dypaProtocolNumber);
      if (!isValid) {
        setDypaProtocolError(error);
        return;
      }
    }
    
    setSubmitting(true)
    setError(null)
    setFriendError(null)
    setSocialSecurityFundError(null)
    
    try {
      // Set dypaProtocolNumber to empty string if the selected fund is not ΔΥΠΑ
      const finalDypaProtocolNumber = 
        selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' 
          ? dypaProtocolNumber 
          : '';
      
      // Create the base data object - start empty
      const baseData: any = {};
      
      // Only include friend_registration_numbers if status allows editing them
      const canEditFriends = !['completed', 'cancelled'].includes(booking.status);
      
      if (canEditFriends) {
        baseData.friend_registration_numbers = friendRegistrationNumbers;
      }
      
      // Only include social security fund fields if the booking status allows editing them
      const isStatusAllowingFundEdit = !['info-verified', 'confirmed', 'completed', 'cancelled'].includes(booking.status);
      
      // Include arrival timeslot if status is confirmed or completed
      if (booking.status === 'confirmed' || booking.status === 'completed') {
        baseData.arrival_timeslot_id = selectedArrivalTimeslot || undefined;
      }
      
      if (isStatusAllowingFundEdit) {
        // Include social security fund ID only if the status allows editing
        baseData.social_security_fund_id = selectedSocialSecurityFund || undefined;
        
        // Include DYPA protocol number only if applicable and status allows editing
        if (selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ') {
          baseData.dypa_protocol_number = finalDypaProtocolNumber;
        } else {
          baseData.dypa_protocol_number = '';
        }
        
        // Include sub-fund only if applicable and status allows editing
        if (selectedSocialSecurityFund && hasSocialSecuritySubFunds && selectedSocialSecuritySubFund) {
          baseData.social_security_sub_fund_id = selectedSocialSecuritySubFund;
        } else {
          baseData.social_security_sub_fund_id = undefined;
        }
      } else if (booking.status !== 'completed' && booking.status !== 'cancelled') {
        // For 'info-verified' and 'confirmed' statuses, only include DYPA protocol number if needed
        // but don't include the fund IDs to avoid the API error
        if (selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && booking.status !== 'confirmed') {
          baseData.dypa_protocol_number = finalDypaProtocolNumber;
        }
      }
      
      const result = await updateBooking(parseInt(id, 10), baseData)
      
      // Update the local booking state with the server response
      setBooking(result);
      
      // Update the original friend registration numbers to match the current ones
      // This will hide the "Added Friends" section after saving
      setOriginalFriendRegistrationNumbers([...friendRegistrationNumbers]);
      
      navigate('/') // Go back to home page after successful update
    } catch (err: any) {
      // Check for specific error messages from the API
      if (err.response?.data) {
        const errorData = err.response.data;
        
        // Check for friend-related errors
        if (errorData.friend_registration_numbers) {
          const friendError = Array.isArray(errorData.friend_registration_numbers) 
            ? errorData.friend_registration_numbers[0] 
            : errorData.friend_registration_numbers;
          
          // Check for gender validation error
          if (friendError.includes('same gender')) {
            setFriendError(t('errors.friendGenderMismatch', { ns: 'bookings' }))
          } 
          // Check for invalid registration numbers
          else if (friendError.includes('Invalid registration numbers')) {
            setFriendError(t('errors.invalidRegistrationNumbers', { ns: 'bookings' }))
          }
          // Check for age difference error
          else if (friendError.includes('within 1.5 years of age difference')) {
            setFriendError(t('errors.ageDifference', { ns: 'bookings' }))
          }
          // Check for no booking error
          else if (friendError.includes('must have their own booking for the same period')) {
            setFriendError(t('errors.noBooking', { ns: 'bookings' }))
          }
          // Check for cancelled booking error
          else if (friendError.includes('must have an active booking')) {
            setFriendError(t('errors.cancelledBooking', { ns: 'bookings' }))
          }
          // Other friend-related errors
          else {
            setFriendError(friendError)
          }
        } 
        // Check for social security fund errors
        else if (errorData.social_security_fund) {
          const fundError = Array.isArray(errorData.social_security_fund) 
            ? errorData.social_security_fund[0] 
            : errorData.social_security_fund;
          
          // Check if it's the one booking per year error
          if (fundError.includes('one booking per year') || fundError.includes('non-private social security fund')) {
            setSocialSecurityFundError(t('errors.oneNonPrivateBookingPerYear', { ns: 'bookings' }));
          } else {
            setSocialSecurityFundError(fundError);
          }
        }
        else {
          setError(t('errors.updateFailed', { ns: 'bookings' }))
        }
      } else {
        setError(t('errors.updateFailed', { ns: 'bookings' }))
      }
    } finally {
      setSubmitting(false)
    }
  }
  
  // Validate registration number format: TR-{year}-{5 digits}
  const isValidRegistrationNumber = (regNumber: string): boolean => {
    const regNumberPattern = /^TR-\d{4}-\d{5}$/;
    return regNumberPattern.test(regNumber);
  }

  const addFriendRegistration = () => {
    if (!friendRegistrationInput.trim()) return
    
    // Check if already in the list
    if (friendRegistrationNumbers.includes(friendRegistrationInput.trim())) {
      setFriendError(t('errors.duplicateFriend', { ns: 'bookings' }))
      return
    }
    
    // Validate registration number format
    const regNumber = friendRegistrationInput.trim()
    if (!isValidRegistrationNumber(regNumber)) {
      setFriendError(t('errors.invalidRegistrationFormat', { ns: 'bookings' }))
      return
    }
    
    // Check if the registration number belongs to the booking's child
    if (booking && children.length > 0) {
      const bookingChild = children.find(child => child.id === booking.child)
      if (bookingChild && bookingChild.registration_number === regNumber) {
        setFriendError(t('errors.cannotAddSelf', { ns: 'bookings', defaultValue: "You cannot add the child as their own friend" }))
        return
      }
    }
    
    // Clear any friend errors when adding a new friend
    setFriendError(null)
    
    // First update the local state with all existing friends plus the new one
    const updatedFriendRegistrationNumbers = [...friendRegistrationNumbers, regNumber];
    setFriendRegistrationNumbers(updatedFriendRegistrationNumbers)
    
    // Then update the booking state if it exists
    if (booking) {
      // Create a deep copy of the booking
      const updatedBooking = JSON.parse(JSON.stringify(booking));
      
      // Initialize friend_registration_numbers if it doesn't exist
      if (!updatedBooking.friend_registration_numbers) {
        updatedBooking.friend_registration_numbers = [];
      }
      
      // Set the friend_registration_numbers to match the local state
      updatedBooking.friend_registration_numbers = [...updatedFriendRegistrationNumbers];
      
      // Update the booking state
      setBooking(updatedBooking);
    }
    
    setFriendRegistrationInput('')
  }
  
  const removeFriendRegistration = (index: number) => {
    // Clear any friend errors when removing a friend
    setFriendError(null)
    
    // Get the registration number that's being removed
    const regNumberToRemove = friendRegistrationNumbers[index];
    
    // First update the local state
    const updatedFriendRegistrationNumbers = [...friendRegistrationNumbers];
    updatedFriendRegistrationNumbers.splice(index, 1);
    setFriendRegistrationNumbers(updatedFriendRegistrationNumbers);
    
    // If the removed registration number was in the original list,
    // we should also remove it from originalFriendRegistrationNumbers
    // to maintain the correct relationship between the two arrays
    if (originalFriendRegistrationNumbers.includes(regNumberToRemove)) {
      const updatedOriginalNumbers = [...originalFriendRegistrationNumbers];
      const originalIndex = updatedOriginalNumbers.indexOf(regNumberToRemove);
      if (originalIndex !== -1) {
        updatedOriginalNumbers.splice(originalIndex, 1);
        setOriginalFriendRegistrationNumbers(updatedOriginalNumbers);
      }
    }
    
    // Then update the booking state if it exists
    if (booking) {
      // Create a deep copy of the booking
      const updatedBooking = JSON.parse(JSON.stringify(booking));
      
      // Initialize friend_registration_numbers if it doesn't exist
      if (!updatedBooking.friend_registration_numbers) {
        updatedBooking.friend_registration_numbers = [];
      } else {
        // Set the friend_registration_numbers to match the local state
        updatedBooking.friend_registration_numbers = [...updatedFriendRegistrationNumbers];
      }
      
      // Update the booking state
      setBooking(updatedBooking);
    }
  }
  
  // Function to copy booking code to clipboard
  const copyBookingCode = () => {
    if (booking && booking.code) {
      navigator.clipboard.writeText(booking.code)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy booking code:', err);
        });
    }
  }
  
  // Get child name from booking
  const getChildName = (childId?: number) => {
    if (!childId) return t('bookingsBox.unknownChild')
    const child = children.find(child => child.id === childId)
    return child ? `${child.first_name} ${child.last_name}` : t('bookingsBox.unknownChild')
  }
  
  // Get child gender
  const getChildGender = (childId?: number) => {
    if (!childId) return null
    const child = children.find(child => child.id === childId)
    return child ? child.gender : null
  }
  
  // Get gender-based styling for child
  const getGenderStyling = (gender: string | null) => {
    if (gender === 'female') {
      return 'bg-pink-100 text-pink-800 border-pink-200';
    } else if (gender === 'male') {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else {
      return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }
  
  // Get status badge color based on status
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'finalized':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'interested':
      case 'awaiting-communication':
      case 'info-verified':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'confirmed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'detail_checking':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'completed':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }
  
  // Copy booking code to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      });
  };

  // Remove a masked friend
  const removeMaskedFriend = (index: number) => {
    if (!booking || !booking.masked_friends) return;
    
    // Create a deep copy of the booking
    const updatedBooking = JSON.parse(JSON.stringify(booking));
    
    // Create a copy of masked_friends array and remove the friend at the specified index
    if (!updatedBooking.masked_friends) {
      updatedBooking.masked_friends = [];
    } else {
      updatedBooking.masked_friends.splice(index, 1);
    }
    
    // If the booking has friend_registration_numbers, update those too
    if (updatedBooking.friend_registration_numbers) {
      // Only remove if the index is valid
      if (index < updatedBooking.friend_registration_numbers.length) {
        // Get the registration number that's being removed
        const regNumberToRemove = updatedBooking.friend_registration_numbers[index];
        
        // Create a copy of the friend_registration_numbers array
        const updatedRegNumbers = [...updatedBooking.friend_registration_numbers];
        updatedRegNumbers.splice(index, 1);
        updatedBooking.friend_registration_numbers = updatedRegNumbers;
        
        // Update the local state for friend registration numbers
        setFriendRegistrationNumbers([...updatedRegNumbers]);
        
        // If the removed registration number was in the original list,
        // we should also remove it from originalFriendRegistrationNumbers
        if (originalFriendRegistrationNumbers.includes(regNumberToRemove)) {
          const updatedOriginalNumbers = [...originalFriendRegistrationNumbers];
          const originalIndex = updatedOriginalNumbers.indexOf(regNumberToRemove);
          if (originalIndex !== -1) {
            updatedOriginalNumbers.splice(originalIndex, 1);
            setOriginalFriendRegistrationNumbers(updatedOriginalNumbers);
          }
        }
      }
    }
    
    // Update the booking state
    setBooking(updatedBooking);
    
    // Clear any friend errors
    setFriendError(null);
  };
  
  // Check if there are newly added friends that haven't been saved yet
  const hasNewlyAddedFriends = () => {
    // Get only the friends that are in friendRegistrationNumbers but not in originalFriendRegistrationNumbers
    const newlyAddedFriends = friendRegistrationNumbers.filter(
      regNum => !originalFriendRegistrationNumbers.includes(regNum)
    );
    
    // Return true if there are any newly added friends
    return newlyAddedFriends.length > 0;
  };
  
  // New function to handle booking cancellation
  const handleCancelBooking = async () => {
    if (!id) return;
    
    setIsCancelling(true);
    setError(null);
    
    try {
      await deleteBooking(parseInt(id, 10));
      // Navigate back to home page after successful cancellation
      navigate('/');
    } catch (err: any) {
      console.error('Error cancelling booking:', err);
      setError(t('errors.cancelFailed', { ns: 'bookings', defaultValue: 'Failed to cancel booking' }));
      setShowCancelModal(false);
    } finally {
      setIsCancelling(false);
    }
  };
  
  // Validate DYPA protocol number format: XX/YYYY/XXXXXXXXXXXX where YYYY must be the current year
  const validateDypaProtocolNumber = (value: string): { isValid: boolean; error: string | null } => {
    if (!value) return { isValid: true, error: null }; // Empty is allowed
    
    const pattern = /^(\d{2})\/(\d{4})\/(\d{12})$/;
    const match = value.match(pattern);
    
    if (!match) {
      return {
        isValid: false,
        error: t('errors.invalidDypaFormat', { 
          ns: 'bookings', 
          defaultValue: 'DYPA protocol number must be in format: XX/YYYY/XXXXXXXXXXXX' 
        })
      };
    }
    
    // Extract the year from the protocol number
    const year = parseInt(match[2], 10);
    const currentYear = new Date().getFullYear();
    
    if (year !== currentYear) {
      return {
        isValid: false,
        error: t('errors.invalidDypaYear', { 
          ns: 'bookings', 
          defaultValue: `DYPA protocol year must be ${currentYear}`,
          year: currentYear
        })
      };
    }
    
    return { isValid: true, error: null };
  };
  
  const handleDypaProtocolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDypaProtocolNumber(value);
    // Clear error when user types
    setDypaProtocolError(null);
  };
  
  if (loading) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <NavigationBar />
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        </div>
      </div>
    )
  }
  
  if (!booking) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <NavigationBar />
        <div className="max-w-2xl mx-auto py-12 px-4">
          <div className="card-neobrutalism p-8">
            <div className="text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-neobrutalism-text mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <h2 className="text-2xl font-black text-neobrutalism-text mb-4">
                {t('errors.bookingNotFound', { ns: 'bookings' })}
              </h2>
              <div className="mt-6">
                <Link to="/" className="btn-neobrutalism bg-neobrutalism-green text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4">
                  {t('common:backToHome')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div style={{ minHeight: '100vh' }}>
      <NavigationBar />
      
      <main className="max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="card-neobrutalism">
          <div className="flex items-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2 text-neobrutalism-text" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            <h1 className="text-2xl font-black text-neobrutalism-text">
              {t('bookingsBox.editBooking')}
            </h1>
          </div>
          
          <div className="mb-6 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1 sm:gap-0">
              <span className="font-light mb-1 sm:mb-0">{t('bookingsBox.code')}:</span>
              <div>
                <span className="px-2 py-1 text-sm font-medium rounded-full bg-purple-100 text-purple-800 border-purple-200 border-2 inline-flex items-center">
                  {booking.code}
                  <button 
                    onClick={() => copyToClipboard(booking.code)}
                    className="ml-2 text-purple-700 hover:text-purple-900 focus:outline-none"
                    aria-label="Copy booking code"
                  >
                    {copySuccess ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </span>
                {copySuccess && (
                  <span className="ml-2 text-xs text-green-600 animate-fade-in-out">
                    {t('common.copied', { defaultValue: 'Copied!' })}
                  </span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1 sm:gap-0">
              <span className="font-light mb-1 sm:mb-0">{t('bookingsBox.child')}:</span>
              <div>
                {booking.child ? (
                  <Link 
                    to={`/children/${booking.child}`} 
                    className={`px-2 py-1 text-sm font-medium border-2 rounded-full ${getGenderStyling(getChildGender(booking.child))}`}
                  >
                    {getChildName(booking.child)}
                  </Link>
                ) : (
                  <span className="font-light">{t('bookingsBox.unknownChild')}</span>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1 sm:gap-0">
              <span className="font-light mb-1 sm:mb-0">{t('bookingsBox.event')}:</span>
              <div>
                <span className="px-2 py-1 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800 border-yellow-200 border-2">
                  {booking.event_name || t('bookingsBox.unknownEvent')}
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start sm:items-center gap-1 sm:gap-0">
              <span className="font-light mb-1 sm:mb-0">{t('bookingsBox.status')}:</span>
              <div>
                <span className={`px-2 py-1 text-sm font-medium rounded-full ${getStatusColor(booking.status)}`}>
                  {t(`bookingsBox.statuses.${booking.status}`)}
                </span>
              </div>
            </div>

            
            {booking.in_waiting_list && (
            <div className="grid grid-cols-1 sm:grid-cols items-start sm:items-center gap-1 sm:gap-0">
              <span className="text-neobrutalism-red font-bold flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {t('bookingsBox.waitingList')}
              </span>
            </div>
            )}
          </div>
          
          {error && (
            <div className="bg-neobrutalism-red p-4 rounded-lg border-2 border-black mb-4">
              <p className="text-neobrutalism-text font-bold">{error}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Social Security Fund Selection */}
            <div>
              <label htmlFor="social-security-fund" className="block font-bold mb-2">
                {t('bookingsBox.socialSecurityRegistry')}
              </label>
              <SocialSecurityFundSelect
                funds={socialSecurityFunds}
                value={selectedSocialSecurityFund}
                onChange={(value) => {
                  setSelectedSocialSecurityFund(value);
                  setSocialSecurityFundError(null);
                }}
                placeholder={t('bookingsBox.selectSocialSecurityRegistry')}
                className={`w-full ${socialSecurityFundError ? 'border-2 border-neobrutalism-red' : ''}`}
                isDisabled={booking.status === 'info-verified' || booking.status === 'confirmed' || booking.status === 'completed' || booking.status === 'cancelled'}
              />
              {socialSecurityFundError && (
                <p className="text-neobrutalism-red mt-1 font-bold">
                  {socialSecurityFundError}
                </p>
              )}
            </div>
            
            {/* Social Security Sub Fund Selection - Only show if the selected fund has sub-funds */}
            {selectedSocialSecurityFund && hasSocialSecuritySubFunds && (
              <div>
                <label htmlFor="social-security-sub-fund" className="block font-bold mb-2">
                  {t('bookingsBox.socialSecurityFund')} {hasSocialSecuritySubFunds && '*'}
                </label>
                <SocialSecuritySubFundSelect
                  subFunds={subFunds}
                  value={selectedSocialSecuritySubFund}
                  onChange={setSelectedSocialSecuritySubFund}
                  placeholder={t('bookingsBox.selectSocialSecurityFund')}
                  className="w-full"
                  isDisabled={!selectedSocialSecurityFund || !hasSocialSecuritySubFunds || booking.status === 'info-verified' || booking.status === 'confirmed' || booking.status === 'completed' || booking.status === 'cancelled'}
                />
                {selectedSocialSecurityFund && hasSocialSecuritySubFunds && !selectedSocialSecuritySubFund && (
                  <p className="text-neobrutalism-red mt-1 font-bold">
                    {t('errors.socialSecurityFundRequired', { ns: 'bookings', defaultValue: 'Πρέπει να επιλέξετε Ταμείο Φορέα για αυτόν τον Φορέα Ασφάλισης' })}
                  </p>
                )}
              </div>
            )}
            
            {/* DYPA Protocol Number */}
            {selectedSocialSecurityFund && selectedFund?.name === 'ΔΥΠΑ' && (
              <div>
                <label htmlFor="dypa-protocol-number" className="block font-bold mb-2">
                  {t('bookingsBox.dypaProtocolNumber')}
                </label>
                <input
                  type="text"
                  id="dypa-protocol-number"
                  value={dypaProtocolNumber}
                  onChange={handleDypaProtocolChange}
                  className={`w-full p-2 border-2 ${dypaProtocolError ? 'border-neobrutalism-red' : 'border-black'}`}
                  placeholder={`XX/${new Date().getFullYear()}/XXXXXXXXXXXX`}
                  disabled={booking.status === 'confirmed' || booking.status === 'completed' || booking.status === 'cancelled'}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('bookingsBox.dypaProtocolFormat', { ns: 'bookings' })}: XX/{new Date().getFullYear()}/XXXXXXXXXXXX
                </p>
                {dypaProtocolError && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{dypaProtocolError}</p>
                )}
              </div>
            )}
            
            {/* Arrival Time Slot Selection - Only show if status is confirmed or completed */}
            {canSelectArrivalTimeslot && arrivalTimeslots.length > 0 && (
              <div>
                <label htmlFor="arrival-timeslot" className="block font-bold mb-2">
                  {t('bookingsBox.arrivalAppointment', { ns: 'bookings', defaultValue: 'Arrival Appointment' })}
                </label>
                <select
                  id="arrival-timeslot"
                  value={selectedArrivalTimeslot || ''}
                  onChange={(e) => setSelectedArrivalTimeslot(e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="w-full p-2 border-2 border-black rounded"
                  disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                >
                  <option value="">
                    {t('bookingsBox.selectArrivalTime', { ns: 'bookings', defaultValue: 'Select a time slot' })}
                  </option>
                  {arrivalTimeslots
                    .filter(timeslot => !timeslot.is_full)  // Only show timeslots that are not full
                    .map((timeslot) => {
                      const startTime = new Date(timeslot.start_time);
                      const endTime = new Date(timeslot.end_time);
                      // Display full datetime with date and time
                      const dateLabel = startTime.toLocaleDateString('el-GR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      });
                      const timeLabel = `${startTime.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}`;
                      
                      return (
                        <option key={timeslot.id} value={timeslot.id}>
                          {dateLabel} {timeLabel}
                        </option>
                      );
                    })}
                </select>
              </div>
            )}
            
            {/* Friends - Only show when status allows editing */}
            {(booking.status as string) !== 'completed' && (booking.status as string) !== 'cancelled' && (
            <div>
              <label className="block font-bold mb-2">
                {t('bookingsBox.friends')}
              </label>
              
              <div className="mt-2 flex">
                <input
                  type="text"
                  value={friendRegistrationInput}
                  onChange={(e) => {
                    // Clear friend error when user types
                    if (friendError) setFriendError(null);
                    setFriendRegistrationInput(e.target.value);
                  }}
                  placeholder={t('bookingsBox.friendRegistrationPlaceholder')}
                  className={`w-full p-1 sm:p-2 text-xs sm:text-base border-2 ${friendError ? 'border-neobrutalism-red' : 'border-black'}`}
                  disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                />
                <button
                  type="button"
                  onClick={addFriendRegistration}
                  className="ml-2 btn-neobrutalism bg-neobrutalism-accent text-xs sm:text-base py-1 sm:py-2 px-2 sm:px-4"
                  disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                >
                  {t('bookingsBox.add')}
                </button>
              </div>
              
              <p className="text-xs text-gray-500 mt-1">
                {t('bookingsBox.registrationNumberFormat')}
              </p>
              
              {friendError && (
                <p className="text-neobrutalism-red mt-1 font-bold">{friendError}</p>
              )}
              
              {/* Only show the Added Friends section if there are newly added friends */}
              {hasNewlyAddedFriends() && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {t('bookingsBox.addedFriends')}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {friendRegistrationNumbers
                      .filter(regNum => !originalFriendRegistrationNumbers.includes(regNum))
                      .map((regNum, index) => (
                        <div 
                          key={index} 
                          className={`flex items-center rounded-full px-3 py-1 border ${getGenderStyling(getChildGender(booking.child))}`}
                        >
                          <span className="text-sm font-medium mr-2">{regNum}</span>
                          <button
                            type="button"
                            onClick={() => {
                              // Find the index in the full friendRegistrationNumbers array
                              const fullIndex = friendRegistrationNumbers.indexOf(regNum);
                              if (fullIndex !== -1) {
                                removeFriendRegistration(fullIndex);
                              }
                            }}
                            className="text-gray-500 hover:text-neobrutalism-red focus:outline-none"
                            aria-label={t('bookingsBox.remove')}
                            disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              
              {booking.masked_friends && booking.masked_friends.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {t('bookingsBox.maskedFriends')}:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {booking.masked_friends.map((name, index) => {
                      // Since we don't have direct access to the friend's gender,
                      // we can infer it from the booking's child gender since friends must be the same gender
                      const childGender = getChildGender(booking.child);
                      return (
                        <div 
                          key={index} 
                          className={`flex items-center rounded-full px-3 py-1 border ${getGenderStyling(childGender)}`}
                        >
                          <span className="text-sm font-medium mr-2">{name}</span>
                          <button
                            type="button"
                            onClick={() => removeMaskedFriend(index)}
                            className="text-gray-500 hover:text-neobrutalism-red focus:outline-none"
                            aria-label={t('bookingsBox.remove')}
                            disabled={booking.status === 'completed' || booking.status === 'cancelled'}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            )}
            
            {/* Show existing friends if status doesn't allow editing */}
            {((booking.status as string) === 'completed') && booking.masked_friends && booking.masked_friends.length > 0 && (
              <div>
                <label className="block font-bold mb-2">
                  {t('bookingsBox.maskedFriends')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {booking.masked_friends.map((name, index) => {
                    const childGender = getChildGender(booking.child);
                    return (
                      <div 
                        key={index} 
                        className={`flex items-center rounded-full px-3 py-1 border ${getGenderStyling(childGender)}`}
                      >
                        <span className="text-sm font-medium">{name}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('bookingsBox.friendsCannotBeChangedAfterCompletion', { ns: 'bookings', defaultValue: 'Friends cannot be changed after booking is completed' })}
                </p>
              </div>
            )}
            
            {/* Fund-specific Information Section */}
            {selectedSocialSecurityFund && (
              <BookingInfoSection fundName={selectedFund?.name} />
            )}
            
            <div className="flex flex-row gap-4 pt-4">
              <Link to="/" className="btn-neobrutalism text-center bg-yellow-500 text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4 w-1/3 flex items-center justify-center">
                {t('modal.back', { ns: 'bookings', defaultValue: 'Back' })}
              </Link>
              
              {(booking.status !== 'completed' && booking.status !== 'cancelled') && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowCancelModal(true)}
                    className="btn-neobrutalism bg-neobrutalism-red text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4 w-1/3 flex items-center justify-center"
                  >
                    {t('modal.cancelBooking', { ns: 'bookings', defaultValue: 'Cancel Booking' })}
                  </button>
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-neobrutalism bg-neobrutalism-green text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4 w-1/3 flex items-center justify-center"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('buttons.saving')}
                      </>
                    ) : (
                      t('bookingsBox.saveChanges')
                    )}
                  </button>
                </>
              )}
              
              {(booking.status === 'completed' || booking.status === 'cancelled') && (
                <div className="w-2/3"></div> // Spacer div to keep the back button aligned left
              )}
            </div>
          </form>
        </div>
      </main>
      
      {/* Cancel Confirmation Modal */}
      <CancelConfirmationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleCancelBooking}
        isCancelling={isCancelling}
      />
    </div>
  )
} 